import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirty - Unsanitized HTML string
 * @param {object} options - DOMPurify configuration options
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtml = (dirty, options = {}) => {
  if (!dirty || typeof dirty !== 'string') return '';
  
  const defaultConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    ...options
  };
  
  return DOMPurify?.sanitize(dirty, defaultConfig);
};

/**
 * Sanitize plain text by removing all HTML tags
 * @param {string} text - Text that may contain HTML
 * @returns {string} - Plain text without HTML
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return DOMPurify?.sanitize(text, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true
  });
};

/**
 * Escape HTML special characters
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return text?.replace(/[&<>"'\/]/g, (char) => map?.[char]);
};

/**
 * Validate and sanitize user input
 * @param {string} input - User input
 * @param {object} options - Validation options
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input, options = {}) => {
  if (!input || typeof input !== 'string') return '';
  
  const {
    maxLength = 5000,
    allowHtml = false,
    removeControlChars = true
  } = options;
  
  let sanitized = input;
  
  // Remove control characters (null bytes, line breaks in single-line inputs)
  if (removeControlChars) {
    sanitized = sanitized?.replace(/[\r\n\0]/g, '');
  }
  
  // Trim to max length
  if (maxLength && sanitized?.length > maxLength) {
    sanitized = sanitized?.substring(0, maxLength);
  }
  
  // Sanitize HTML if not allowed
  if (!allowHtml) {
    sanitized = sanitizeText(sanitized);
  } else {
    sanitized = sanitizeHtml(sanitized);
  }
  
  return sanitized;
};

/**
 * Sanitize URL to prevent javascript: and data: protocols
 * @param {string} url - URL to sanitize
 * @returns {string} - Sanitized URL or empty string if invalid
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  
  const trimmed = url?.trim()?.toLowerCase();
  
  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  if (dangerousProtocols?.some(protocol => trimmed?.startsWith(protocol))) {
    return '';
  }
  
  return url;
};

export default {
  sanitizeHtml,
  sanitizeText,
  escapeHtml,
  sanitizeInput,
  sanitizeUrl
};